import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical, Loader2, Plus, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useToast } from '../shared/Toast';
import { formatMoney } from '../../utils/formatMoney';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField } from '../settings/sheet';
import {
  ASIDE_BTN, Column, DataTable, DetailPanel, EmptyState, GHOST_BTN, ICON_BTN, INPUT_CLS, ListToolbar, PRIMARY_BTN, SELECT_CLS,
  Stat, Stats,
} from '../shared/listPage';
import { BlockTitle } from './warehouseUi';

/**
 * Αποθήκη → «Υλικά / Συνταγές» (14/10/2026): τα υλικά (συνδυασμοί πρώτων υλών) σε πίνακα + συρτάρι με τη
 * συνταγή. Ίδια αιτήματα με πριν: GET /api/warehouse/raw-materials και /ingredients, GET /ingredients/:id (τα
 * συστατικά, όταν ανοίγει ένα υλικό), POST /ingredients ή PUT /ingredients/:id με { name, unit, components }.
 */

interface RawMaterial {
  id: number;
  name: string;
  current_stock: number;
  purchase_unit: string;
  purchase_price: number;
}

interface Ingredient {
  id?: number;
  name: string;
  components: {
    raw_material_id: number;
    quantity: number;
    unit: string;
  }[];
  unit: string;
  cost_per_unit?: number;
}

type DetailState = 'idle' | 'loading' | 'error';

const emptyIngredient = (): Ingredient => ({ name: '', components: [], unit: 'ΤΕΜΑΧΙΑ' });

const SimpleIngredientManager: React.FC = () => {
  const { t } = useTranslation(['warehouse', 'common', 'listPage']);
  const { showToast } = useToast();
  const uid = useId();
  const formId = `${uid}-form`;
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<'ready' | 'error' | 'forbidden'>('ready');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
  const [detailState, setDetailState] = useState<DetailState>('idle');
  const [newIngredient, setNewIngredient] = useState<Ingredient>(emptyIngredient);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const greekUnits = [
    { value: 'ΤΕΜΑΧΙΑ', label: t('warehouse:unitLabelTemachia') },
    { value: 'ΚΙΛΑ', label: t('warehouse:unitLabelKila') },
    { value: 'ΛΙΤΡΑ', label: t('warehouse:unitLabelLitra') }
  ];

  useEffect(() => {
    fetchRawMaterials();
    fetchIngredients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('xentrix_token');
      const response = await fetch(`${API_BASE_URL}/api/warehouse/raw-materials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRawMaterials(data.rawMaterials || []);
      }
    } catch (error) {
      console.error('Error fetching raw materials:', error);
    }
  };

  const fetchIngredients = async () => {
    try {
      const token = localStorage.getItem('xentrix_token');
      const response = await fetch(`${API_BASE_URL}/api/warehouse/ingredients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIngredients(data || []);
        setLoadState('ready');
      } else {
        setLoadState(response.status === 403 ? 'forbidden' : 'error');
      }
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      setLoadState('error');
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    setLoading(true);
    fetchRawMaterials();
    fetchIngredients();
  };

  const changeIngredient = (next: Ingredient) => {
    setFormError(null);
    setNewIngredient(next);
  };

  const handleAddComponent = () => {
    changeIngredient({
      ...newIngredient,
      components: [...newIngredient.components, {
        raw_material_id: 0,
        quantity: 0,
        unit: ''
      }]
    });
  };

  const handleRemoveComponent = (index: number) => {
    changeIngredient({
      ...newIngredient,
      components: newIngredient.components.filter((_, i) => i !== index)
    });
  };

  const handleComponentChange = (index: number, field: string, value: any) => {
    const updated = [...newIngredient.components];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-fill unit when material is selected
    if (field === 'raw_material_id') {
      const material = rawMaterials.find(m => m.id === parseInt(value));
      if (material) {
        updated[index].unit = material.purchase_unit;
      }
    }

    changeIngredient({
      ...newIngredient,
      components: updated
    });
  };

  // Το select δίνει το id ως κείμενο («5»): η σύγκριση με αριθμό, αλλιώς ένα υλικό που μόλις διαλέχτηκε
  // έβγαζε κόστος 0 (ως 14/10). Μόνο η προεπισκόπηση — το σώμα του αιτήματος μένει ίδιο.
  const calculateIngredientCost = () => {
    return newIngredient.components.reduce((total, comp) => {
      const material = rawMaterials.find(m => m.id === Number(comp.raw_material_id));
      if (material) {
        return total + (comp.quantity * parseFloat(material.purchase_price.toString()));
      }
      return total;
    }, 0);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingIngredient(null);
    setDetailState('idle');
    setFormError(null);
    setNewIngredient(emptyIngredient());
  };

  const requestClose = () => {
    if (saving) return;
    closeForm();
  };

  const startCreate = () => {
    setEditingIngredient(null);
    setNewIngredient(emptyIngredient());
    setDetailState('idle');
    setFormError(null);
    setShowCreateForm(true);
  };

  const handleEditIngredient = async (ingredient: Ingredient) => {
    // Το συρτάρι ανοίγει αμέσως· τα συστατικά έρχονται με ένα ακόμη αίτημα (όπως πριν).
    setEditingIngredient(ingredient);
    setNewIngredient({ ...ingredient, components: [] });
    setFormError(null);
    setDetailState('loading');
    setShowCreateForm(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const response = await fetch(`${API_BASE_URL}/api/warehouse/ingredients/${ingredient.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNewIngredient({
          ...ingredient,
          components: (data.components || []).map((c: any) => ({
            raw_material_id: c.raw_material_id,
            quantity: c.quantity_needed,
            unit: c.unit,
            name: c.name
          }))
        });
        setDetailState('idle');
      } else {
        setDetailState('error');
      }
    } catch (error) {
      console.error('Error fetching ingredient details:', error);
      setDetailState('error');
    }
  };

  const handleSaveIngredient = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (saving || detailState !== 'idle') return;
    if (!newIngredient.name || newIngredient.components.length === 0) {
      setFormError(t('warehouse:nameAndComponentRequired'));
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const isEditing = editingIngredient && editingIngredient.id;

      const url = isEditing
        ? `${API_BASE_URL}/api/warehouse/ingredients/${editingIngredient.id}`
        : `${API_BASE_URL}/api/warehouse/ingredients`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newIngredient.name,
          unit: newIngredient.unit,
          components: newIngredient.components.filter(c => c.raw_material_id > 0).map(c => ({
            raw_material_id: c.raw_material_id,
            quantity_needed: c.quantity,
            unit: c.unit
          }))
        })
      });

      if (response.ok) {
        closeForm();
        fetchIngredients();
      } else {
        // Ως 14/10 μια απόρριψη του server δεν έλεγε τίποτα· ίδιο μήνυμα με το σφάλμα δικτύου.
        showToast('error', t('warehouse:failedSaveIngredient'));
      }
    } catch (error) {
      console.error('Error saving ingredient:', error);
      showToast('error', t('warehouse:failedSaveIngredient'));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Ingredient>[] = [
    {
      key: 'name',
      header: t('warehouse:ui.recipes.name'),
      width: 'minmax(200px, 2fr)',
      stack: 'title',
      render: i => <span className="font-medium">{i.name}</span>,
      stackRender: i => i.name,
    },
    {
      key: 'unit',
      header: t('warehouse:ui.recipes.unit'),
      width: 'minmax(110px, 1fr)',
      render: i => <span className="text-t2">{i.unit}</span>,
    },
    {
      key: 'cost',
      header: t('warehouse:ui.recipes.cost'),
      width: 'minmax(140px, 1fr)',
      align: 'right',
      stack: 'end',
      render: i => (
        <span className="font-medium tabular-nums">
          {t('warehouse:ui.recipes.costPer', { cost: formatMoney(parseFloat(i.cost_per_unit?.toString() || '0')), unit: i.unit })}
        </span>
      ),
    },
  ];

  const detailBody = () => {
    if (detailState === 'loading') {
      return (
        <div className="space-y-3 pt-1" data-testid="ingredient-loading">
          {[60, 42, 74].map((w, i) => <span key={i} className="lp-skel" style={{ width: `${w}%` }} />)}
          <span className="sr-only" role="status">{t('listPage:loading')}</span>
        </div>
      );
    }
    if (detailState === 'error' && editingIngredient) {
      return <LoadFailed message={t('warehouse:ui.recipes.detailLoadFailed')} onRetry={() => handleEditIngredient(editingIngredient)} />;
    }
    return (
      <form id={formId} onSubmit={handleSaveIngredient} noValidate className="space-y-6" data-testid="ingredient-form">
        <div className="xs-grid2">
          <SheetField label={t('warehouse:ui.recipes.nameField')} htmlFor={`${uid}-name`}>
            <input
              id={`${uid}-name`}
              type="text"
              placeholder={t('warehouse:ingredientNamePlaceholder')}
              value={newIngredient.name}
              autoComplete="off"
              onChange={e => changeIngredient({ ...newIngredient, name: e.target.value })}
              className={INPUT_CLS}
            />
          </SheetField>
          <SheetField label={t('warehouse:ui.recipes.unit')} htmlFor={`${uid}-unit`}>
            <select
              id={`${uid}-unit`}
              className={SELECT_CLS}
              value={newIngredient.unit}
              onChange={e => changeIngredient({ ...newIngredient, unit: e.target.value })}
            >
              {greekUnits.map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </SheetField>
        </div>

        {/* Components */}
        <div role="group" aria-labelledby={`${uid}-components`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <BlockTitle id={`${uid}-components`}>{t('warehouse:ui.recipes.components')}</BlockTitle>
            <button type="button" onClick={handleAddComponent} className={ASIDE_BTN}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              {t('warehouse:ui.recipes.addComponent')}
            </button>
          </div>

          {newIngredient.components.length === 0 ? (
            <p className="text-sm text-t3 py-4 text-center border border-dashed border-line-strong rounded-xl">
              {t('warehouse:ui.recipes.noComponents')}
            </p>
          ) : (
            <ul className="space-y-3" data-testid="ingredient-components">
              {newIngredient.components.map((component, index) => (
                // eslint-disable-next-line react/no-array-index-key -- τα συστατικά δεν έχουν id (ίδιο κλειδί με πριν)
                <li key={index} className="grid grid-cols-[minmax(0,1fr)_7rem_auto] sm:grid-cols-[minmax(0,1fr)_7rem_6rem_auto] gap-2 items-end">
                  <div className="col-span-3 sm:col-span-1">
                    <label htmlFor={`${uid}-c${index}-material`} className="block text-xs text-t3 mb-1">{t('warehouse:ui.recipes.material')}</label>
                    <select
                      id={`${uid}-c${index}-material`}
                      className={SELECT_CLS}
                      value={component.raw_material_id}
                      onChange={e => handleComponentChange(index, 'raw_material_id', e.target.value)}
                    >
                      <option value={0}>{t('warehouse:selectMaterial')}</option>
                      {rawMaterials.map(mat => (
                        <option key={mat.id} value={mat.id}>
                          {mat.name} ({formatMoney(parseFloat(mat.purchase_price.toString()))}/{mat.purchase_unit})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`${uid}-c${index}-qty`} className="block text-xs text-t3 mb-1">{t('common:quantity')}</label>
                    <input
                      id={`${uid}-c${index}-qty`}
                      type="number"
                      step="0.001"
                      value={component.quantity}
                      onChange={e => handleComponentChange(index, 'quantity', parseFloat(e.target.value))}
                      className={`${INPUT_CLS} tabular-nums`}
                    />
                  </div>
                  <div className="hidden sm:block">
                    <label htmlFor={`${uid}-c${index}-unit`} className="block text-xs text-t3 mb-1">{t('warehouse:unit')}</label>
                    <input id={`${uid}-c${index}-unit`} value={component.unit} disabled readOnly className={`${INPUT_CLS} bg-surface-3 text-t3 cursor-not-allowed`} />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveComponent(index)}
                    aria-label={t('warehouse:ui.recipes.removeComponent')}
                    title={t('warehouse:ui.recipes.removeComponent')}
                    className={`${ICON_BTN} hover:text-red-600 dark:hover:text-red-400`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Cost Preview */}
        {newIngredient.components.length > 0 && (
          <Stats testId="ingredient-cost">
            <Stat
              label={t('warehouse:ui.recipes.estimatedCost', { unit: newIngredient.unit || t('warehouse:unit') })}
              value={formatMoney(calculateIngredientCost())}
            />
          </Stats>
        )}
      </form>
    );
  };

  const firstLoad = loading && ingredients.length === 0;

  return (
    <>
      <ListToolbar
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={startCreate}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('warehouse:ui.recipes.add')}
          </button>
        )}
        summary={loadState === 'ready' && !firstLoad ? t('warehouse:ui.recipes.count', { count: ingredients.length }) : null}
      />

      {loadState !== 'ready' ? (
        <LoadFailed
          message={loadState === 'forbidden' ? t('warehouse:ui.recipes.forbidden') : t('warehouse:ui.recipes.loadFailed')}
          onRetry={retry}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={ingredients}
          rowKey={i => i.id ?? i.name}
          onRowClick={handleEditIngredient}
          selectedKey={showCreateForm ? editingIngredient?.id ?? null : null}
          loading={loading}
          stickyHeader
          ariaLabel={t('warehouse:ui.tabs.recipes')}
          rowLabel={i => t('warehouse:ui.recipes.rowLabel', { name: i.name })}
          testId="ingredients-table"
          empty={<EmptyState icon={FlaskConical} title={t('warehouse:ui.recipes.empty')} hint={t('warehouse:ui.recipes.emptyHint')} />}
        />
      )}

      <DetailPanel
        open={showCreateForm}
        onClose={requestClose}
        title={editingIngredient ? t('warehouse:ui.recipes.editTitle') : t('warehouse:ui.recipes.newTitle')}
        subtitle={editingIngredient ? t('warehouse:updateIngredientDetails') : t('warehouse:combineRawMaterials')}
        width="lg"
        footer={(
          <>
            {formError && <p role="alert" className="basis-full text-sm font-medium text-red-600 dark:text-red-400 text-right">{formError}</p>}
            <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('common:cancel')}</button>
            <button type="submit" form={formId} className={PRIMARY_BTN} disabled={saving || detailState !== 'idle'} aria-busy={saving || undefined}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {editingIngredient ? t('warehouse:ui.save') : t('warehouse:ui.recipes.create')}
            </button>
          </>
        )}
        testId="ingredient-panel"
      >
        {showCreateForm && detailBody()}
      </DetailPanel>
    </>
  );
};

export default SimpleIngredientManager;
